import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField, LoadingContent } from 'components'
import { updateDOTRejection } from 'controllers/vehicles/conformity/index'
import { DOTRejectionReasonTypeDto } from 'dtos/conformity'
import { VehicleConformityDOTRejectionDto, VehicleConformityDto } from 'dtos/vehicles/conformity/index'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { formatDateMoment, toYesNoOrUndefined } from 'utils'
import DownloadLink from './DownloadLink'
import EditDOTRejectionDialog from './EditDOTRejectionDialog'

interface DOTRejectionCardProps {
  dotRejection?: VehicleConformityDOTRejectionDto
  dotRejectionReasonTypes: DOTRejectionReasonTypeDto[]
  isLoading: boolean
  setState: (values: VehicleConformityDto) => void
  vehicleGuid?: string
}

export default function DOTRejectionCard({ dotRejection, dotRejectionReasonTypes, isLoading, setState, vehicleGuid }: DOTRejectionCardProps) {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  return (
    <>
      <EditDOTRejectionDialog
        dotRejectionReasonTypes={dotRejectionReasonTypes}
        initialValues={dotRejection}
        isLoading={isSaving}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsSaving(true)
          updateDOTRejection(vehicleGuid!, values)
            .then(res => {
              enqueueSnackbar('DOT Rejection Saved Successfully!', { variant: 'success' })
              setDialogOpen(false)
              setState(res.value)
            })
            .finally(() => {
              setIsSaving(false)
            })
        }}
        open={dialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h6'>
                  DOT Rejection
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button disabled={isLoading} endIcon={<Edit />} onClick={() => setDialogOpen(true)} variant='text'>
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <LoadingContent isLoading={isLoading}>
              <Grid item xs={12}>
                <LabelField
                  label='Rejection Date'
                  value={dotRejection?.dotRejectionDateTime ? formatDateMoment(dotRejection.dotRejectionDateTime) : undefined}
                />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Rejection Reason' value={dotRejection?.dotRejectionReasonType?.name} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Note' value={dotRejection?.dotRejectionNote} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Rejection Letter' value={<DownloadLink fileName={dotRejection?.dotRejectionLetterFileName} />} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Needs To Be Sent Back To Canada' value={toYesNoOrUndefined(dotRejection?.needsToBeSentBackToCanada)} />
              </Grid>
            </LoadingContent>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**DOT Rejection Card:**

The DOT Rejection Card allows users to enter the date when a vehicle was rejected by the DOT, along with the reasons for the rejection. Users can also upload the rejection letter received from the DOT.

**Edit Button:**

Clicking the Edit Button opens the Edit DOT Rejection Dialog.

**Rejection Date Label:**

Displays the rejection date entered in the Edit DOT Rejection Dialog.

**Rejection Reason Label:**

Displays the selected rejection reason from the Edit DOT Rejection Dialog.

**Note Label:**

Displays any notes entered for the vehicle via the Edit DOT Rejection Dialog.

**Rejection Letter Download Link:**

Clicking “Download” saves the DOT Rejection Letter to the user’s machine.

**Needs To Be Sent Back To Canada Label:**

Displays "Yes" or "No", based on the selection made in the Edit DOT Rejection Dialog.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
